import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Card, CardContent, CardHeader, CircularProgress, Box, Stack, Typography, IconButton, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { useFetch } from '../../hooks/use-fetch';
import { BASE_ADMIN_API_URL } from '../../config';
import dayjs from 'dayjs';

const BOARD_LABELS = {
    CLIENTS: 'Clients',
    CONTACTS: 'Contacts',
    CONTACTS_PRO: 'Contacts Pro',
    TESTS: 'Essais',
};

const BOARD_COLORS = {
    CLIENTS: 'success',
    CONTACTS: 'primary',
    CONTACTS_PRO: 'warning',
    TESTS: 'info',
};

function descendingComparator(a, b, orderBy) {
    const va = a[orderBy];
    const vb = b[orderBy];
    if (vb < va) return -1;
    if (vb > va) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

const columns = [
    { id: 'created_at', label: 'Date', width: 160 },
    { id: 'boards', label: 'Boards', sortable: false },
    { id: 'total_rows', label: 'Total', width: 100 },
];

export default function CrmSnapshotList() {
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('created_at');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const fetchWithAuth = useFetch();

    const load = useCallback(async () => {
        try {
            const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/crm/snapshots`);
            if (!response.ok) throw new Error(await response.text());
            const data = await response.json();
            setSnapshots(data);
        } catch (e) {
            console.error('Failed to load CRM snapshots', e);
        } finally {
            setLoading(false);
        }
    }, [fetchWithAuth]);

    useEffect(() => {
        let cancelled = false;
        load().then(() => { if (cancelled) return; });
        return () => { cancelled = true; };
    }, [load]);

    const handleSync = useCallback(async () => {
        setSyncing(true);
        try {
            const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/crm/sync`, { method: 'POST' });
            if (!response.ok) throw new Error(await response.text());
            await load();
        } catch (e) {
            console.error('CRM sync failed', e);
        } finally {
            setSyncing(false);
        }
    }, [fetchWithAuth, load]);

    const handleDownload = useCallback(async (snapshotId, board) => {
        try {
            const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/crm/snapshots/${snapshotId}/files/${board}`);
            if (!response.ok) throw new Error(await response.text());
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${board.toLowerCase()}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Failed to download file', e);
        }
    }, [fetchWithAuth]);

    const handleSort = useCallback((columnId) => {
        setOrder(prev => orderBy === columnId && prev === 'asc' ? 'desc' : 'asc');
        setOrderBy(columnId);
    }, [orderBy]);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteTarget) return;
        try {
            const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/crm/snapshots/${deleteTarget}`, { method: 'DELETE' });
            if (response.ok || response.status === 204) {
                setSnapshots(prev => prev.filter(s => s.id !== deleteTarget));
            }
        } catch (e) {
            console.error('Failed to delete snapshot', e);
        }
        setDeleteTarget(null);
    }, [deleteTarget, fetchWithAuth]);

    const sorted = useMemo(() => {
        const rows = snapshots.map(s => ({
            ...s,
            total_rows: s.files.reduce((sum, f) => sum + (f.row_count || 0), 0),
        }));
        return rows.sort(getComparator(order, orderBy));
    }, [snapshots, order, orderBy]);

    return (
        <Card>
            <CardHeader
                title="CRM Snapshots"
                action={
                    <Button
                        variant="contained"
                        startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                        onClick={handleSync}
                        disabled={syncing}
                    >
                        {syncing ? 'Syncing...' : 'Sync Monday'}
                    </Button>
                }
            />
            <CardContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : sorted.length === 0 ? (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No snapshots yet. Click "Sync Monday" to create the first one.
                    </Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {columns.map(col => (
                                        <TableCell key={col.id} sx={{ width: col.width }}>
                                            {col.sortable === false ? col.label : (
                                                <TableSortLabel
                                                    active={orderBy === col.id}
                                                    direction={orderBy === col.id ? order : 'asc'}
                                                    onClick={() => handleSort(col.id)}
                                                >
                                                    {col.label}
                                                </TableSortLabel>
                                            )}
                                        </TableCell>
                                    ))}
                                    <TableCell sx={{ width: 48 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sorted.map(snapshot => (
                                    <TableRow key={snapshot.id} hover>
                                        <TableCell>
                                            {dayjs(snapshot.created_at).format('YYYY-MM-DD HH:mm')}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {snapshot.files.map(f => (
                                                    <Chip
                                                        key={f.board}
                                                        label={`${BOARD_LABELS[f.board] || f.board} (${f.row_count || 0})`}
                                                        size="small"
                                                        color={BOARD_COLORS[f.board] || 'default'}
                                                        variant="outlined"
                                                        icon={<DownloadIcon />}
                                                        onClick={() => handleDownload(snapshot.id, f.board)}
                                                    />
                                                ))}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {snapshot.total_rows}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => setDeleteTarget(snapshot.id)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>

            <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>Delete snapshot</DialogTitle>
                <DialogContent>
                    <Typography>This will delete the snapshot and its CSV files from storage. This cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}
